import * as React from 'react'

import {
  AnswerFormFragment,
  ElementFragment,
  type FormAnswersDataCollectionQuery,
} from 'generated/resources/graphql'

import { buildUrl } from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { cellByType } from './cellRenderers'

const MAX_TEXT_CHARACTERS = 50

type FormMembership =
  FormAnswersDataCollectionQuery['forms']['membershipsConnection']['nodes'][number]

export type ColumnRenderer = (item: FormMembership) => {
  type: 'person' | 'text' | 'status' | 'dotTag' | 'longText' | 'tagList'
  value: {
    name?: string
    firstName?: string
    type?: string
    src?: string
    text?: React.ReactNode
    label?: string
    status?: string
    placeholder?: string
    color?: string
    tags?: Array<{ text: string; customColor: string }>
    lines?: number
  }
}

export type SortingField = 'status'

export type TableColumn = {
  label: string
  render: ColumnRenderer
  info?: string
  sorting?: SortingField
}

export enum TableColumnType {
  Participant = 'participant',
  RelatedResource = 'relatedResource',
  Status = 'status',
  Evaluator = 'evaluator',
  FinalResult = 'finalResult',
  FinalScore = 'finalScore',
}

export const ParticipantCell: ColumnRenderer = (item: FormMembership & { _rowIndex?: number }) => {
  const rowIndex = item._rowIndex ?? 0

  if (!item.target) {
    return {
      type: 'text' as const,
      value: {
        text: i18n.t('forms.responses.columns.participant') + ` ${rowIndex + 1}`,
      },
    }
  }

  return {
    type: 'person' as const,
    value: {
      firstName: item.target.firstName,
      lastName: item.target.lastName,
      type: 'person',
      src: buildUrl(item.target.avatar),
    },
  }
}

export const EvaluatorCell: ColumnRenderer = (item: FormMembership) => ({
  type: 'person' as const,
  value: {
    firstName: item.respondent?.firstName,
    lastName: item.respondent?.lastName,
    type: 'person',
    src: buildUrl(item.respondent?.avatar),
  },
})

export const RelatedResourceCell: ColumnRenderer = (item: FormMembership) => ({
  type: 'text' as const,
  value: {
    text: item.materializedForm?.resourceName,
  },
})

export const StatusCell: ColumnRenderer = (item: FormMembership) => ({
  type: 'status' as const,
  value: {
    label:
      item.status === 'completed'
        ? i18n.t('forms.responses.columns.completed')
        : i18n.t('forms.responses.columns.pending'),
    status: item.status === 'completed' ? 'positive' : 'critical',
  },
})

export const DEFAULT_PASS_PERCENTAGE = 50

export const createFinalResultCell =
  (passPercentage?: string | null): ColumnRenderer =>
  (item: FormMembership) => {
    if (item.status !== 'completed') {
      return {
        type: 'text' as const,
        value: { text: '' },
      }
    }

    const threshold = passPercentage ? Number(passPercentage) : DEFAULT_PASS_PERCENTAGE
    const passed = item.score != null && Number(item.score) >= threshold

    return {
      type: 'status' as const,
      value: {
        label: passed
          ? `${String(i18n.t('forms.responses.columns.passed'))}`
          : `${String(i18n.t('forms.responses.columns.failed'))}`,
        status: passed ? 'positive' : 'critical',
      },
    }
  }

/** @deprecated Use createFinalResultCell(passPercentage) instead */
export const FinalResultCell: ColumnRenderer = createFinalResultCell()

export const FinalScoreCell: ColumnRenderer = (item: FormMembership) => ({
  type: 'text' as const,
  value: {
    text: item.score
      ? `${
          Number(item.score) % 1 === 0
            ? Number(item.score)
            : Number(item.score).toFixed(1).replace(/\.0$/, '')
        }%`
      : '',
  },
})

const createQuestionCell = (question: ElementFragment): ColumnRenderer => {
  return (item: FormMembership) => {
    const answer = item.answers.find((ans: AnswerFormFragment) => ans.element?.id === question.id)

    const Renderer = cellByType[question.type]
    if (!Renderer) {
      throw new Error(`No renderer found for question type: ${question.type}`)
    }
    return Renderer({ answer, question })
  }
}

const getElement = (
  elementId: string | null,
  allElements: ElementFragment[]
): ElementFragment | false => {
  if (!elementId) return false
  return allElements.find((element: ElementFragment) => element.id === elementId) || false
}

const calculatePosition = (element: ElementFragment, allElements: ElementFragment[]): string => {
  const section = element.sectionId ? getElement(element.sectionId, allElements) : false
  const position = section
    ? `${section.position + 1}.${element.position + 1}`
    : element.position + 1

  return position.toString()
}

const createQuestionLabel = (question: ElementFragment, allElements: ElementFragment[]): string => {
  const position = calculatePosition(question, allElements)
  const questionText =
    question.label.length > MAX_TEXT_CHARACTERS
      ? question.label.slice(0, MAX_TEXT_CHARACTERS) + '…'
      : question.label

  return `${position}. ${questionText}`
}

export const createQuestionColumns = (elements: ElementFragment[]): TableColumn[] => {
  return elements
    .filter((element: ElementFragment) => element.type !== 'section')
    .map((question: ElementFragment) => ({
      label: createQuestionLabel(question, elements),
      render: createQuestionCell(question),
      info: question.label,
    }))
}
